import { Code, Cpu, Gamepad2, Globe, Server, Smartphone } from 'lucide-react'
import React from 'react'
import { Heading } from '../ui'

/**
 * ProjectFilters component for filtering projects by category and featured status
 *
 * Features:
 * - Category filter buttons with icons
 * - Featured projects toggle
 * - Keyboard navigation support
 * - ARIA labels for accessibility
 * - Responsive layout
 */

export interface ProjectFiltersProps {
  selectedCategory: string
  showFeaturedOnly: boolean
  onCategoryChange: (categoryId: string) => void
  onFeaturedToggle: (featured: boolean) => void
  className?: string
}

const categories = [
  { id: 'all', label: 'All Projects', icon: Code },
  { id: 'enterprise', label: 'Enterprise Systems', icon: Server },
  { id: 'mobile-games', label: 'Mobile Games', icon: Smartphone },
  { id: 'console-games', label: 'Console Games', icon: Gamepad2 },
  { id: 'web-development', label: 'Web Development', icon: Globe },
  { id: 'personal-projects', label: 'Personal Projects', icon: Cpu },
]

export const ProjectFilters = React.forwardRef<
  HTMLDivElement,
  ProjectFiltersProps
>(
  (
    {
      selectedCategory,
      showFeaturedOnly,
      onCategoryChange,
      onFeaturedToggle,
      className = '',
    },
    ref
  ) => {
    const handleKeyDown = (event: React.KeyboardEvent, categoryId: string) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onCategoryChange(categoryId)
      }
    }

    return (
      <div ref={ref} className={`filters-section ${className}`}>
        <div className="filters-header">
          <Heading as="h2" size="lg" variant="default">
            Filter Projects
          </Heading>
          <div className="featured-toggle">
            <input
              type="checkbox"
              id="featured-only"
              checked={showFeaturedOnly}
              onChange={e => onFeaturedToggle(e.target.checked)}
              aria-describedby="featured-description"
            />
            <label htmlFor="featured-only">Featured Projects Only</label>
            <span id="featured-description" className="sr-only">
              Toggle to show only highlighted projects
            </span>
          </div>
        </div>

        <div
          className="category-filters"
          role="group"
          aria-label="Filter projects by category"
        >
          {categories.map(category => {
            const IconComponent = category.icon
            const isActive = selectedCategory === category.id

            return (
              <button
                key={category.id}
                className={`filter-button ${isActive ? 'active' : ''}`}
                onClick={() => onCategoryChange(category.id)}
                onKeyDown={e => handleKeyDown(e, category.id)}
                aria-pressed={isActive}
                aria-label={`Filter by ${category.label}`}
                type="button"
              >
                <IconComponent aria-hidden="true" />
                {category.label}
              </button>
            )
          })}
        </div>
      </div>
    )
  }
)

ProjectFilters.displayName = 'ProjectFilters'

export default ProjectFilters
